import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../integrations/supabase/client';

const { FiCoffee, FiDroplet, FiThermometer, FiHeart } = FiIcons;

const BrewingGuide = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [activeStep, setActiveStep] = useState(null);
  const [content, setContent] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      const { data } = await supabase.from('page_content').select('*').eq('page', 'products').eq('section', 'brewing');
      if (data) {
        const contentMap = {};
        data.forEach(item => {
          contentMap[item.key] = isRTL ? (item.content_ar || item.content_en) : item.content_en;
        });
        setContent(contentMap);
      }
    };
    fetchContent();
  }, [isRTL]);

  const getText = (key, fallbackKey) => content?.[key] || t(fallbackKey);

  const steps = [
    {
      icon: FiCoffee,
      title: t('brewing.step1'),
      description: t('brewing.step1Text')
    },
    {
      icon: FiDroplet,
      title: t('brewing.step2'),
      description: t('brewing.step2Text')
    },
    {
      icon: FiThermometer,
      title: t('brewing.step3'),
      description: t('brewing.step3Text')
    },
    {
      icon: FiHeart,
      title: t('brewing.step4'),
      description: t('brewing.step4Text')
    }
  ];

  return (
    <section id="brewing" className="py-20 bg-gradient-to-br from-amber-50 to-green-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-green-800 mb-6">
            {getText('title', 'brewing.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {getText('subtitle', 'brewing.subtitle')}
          </p>
          <p className="text-lg text-gray-500 mt-4">
            {getText('description', 'brewing.description')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
              onMouseEnter={() => setActiveStep(index)}
              onMouseLeave={() => setActiveStep(null)}
              className="bg-white p-8 rounded-2xl shadow-lg text-center group relative overflow-hidden"
            >
              {/* Animated background effect */}
              {activeStep === index && (
                <motion.div 
                  className="absolute inset-0 bg-gradient-to-br from-green-500 to-amber-500 opacity-10"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.3 }}
                />
              )}
              
              <div className="relative z-10">
                <motion.div
                  className="bg-gradient-to-br from-green-600 to-green-700 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300"
                  animate={activeStep === index ? { 
                    scale: [1, 1.1, 1],
                    rotate: [0, 5, -5, 0]
                  } : {}}
                  transition={{ duration: 0.5 }}
                >
                  <SafeIcon icon={step.icon} className="w-8 h-8 text-white" />
                </motion.div>
                <div className="bg-green-100 text-green-800 font-bold text-sm px-4 py-1 rounded-full inline-block mb-4">
                  {isRTL ? `${index + 1} الخطوة` : `Step ${index + 1}`}
                </div>
                <h3 className="text-xl font-bold text-green-800 mb-3">
                  {step.title}
                </h3>
                <p className="text-gray-600">
                  {step.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-16 bg-gradient-to-br from-green-700 to-green-800 rounded-3xl shadow-2xl p-12 text-white text-center relative overflow-hidden"
        >
          {/* Coffee beans decoration */}
          <div className="absolute top-4 right-4 opacity-20">
            <SafeIcon icon={FiCoffee} className="w-16 h-16" />
          </div>
          <div className="absolute bottom-4 left-4 opacity-20">
            <SafeIcon icon={FiCoffee} className="w-12 h-12" />
          </div>
          
          <h3 className="text-3xl font-bold mb-4 relative z-10">
            {isRTL ? 'نصيحة احترافية' : 'Pro Tip'}
          </h3>
          <p className="text-lg text-green-100 max-w-3xl mx-auto relative z-10">
            {isRTL 
              ? 'للحصول على أفضل النتائج، استخدم الماء البارد دائماً واطحن قهوة بُنديجا طازجة قبل التحضير مباشرة. القهوة التركية الحقيقية يجب ألا تُغلى أبداً - فقط تُسخن حتى تتكون الرغوة.'
              : 'For the best results, always use cold water and grind your Bondeega coffee fresh just before brewing. True Turkish coffee should never boil - only heat until foam forms.'
            }
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default BrewingGuide;